import { useState, useEffect } from 'react';
import { api, fmt } from '../api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import NavIcon from '../components/NavIcon';

export default function Dashboard() {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [reminders, setReminders] = useState([]);

  useEffect(() => {
    api.getDashboard().then(setData).finally(() => setLoading(false));
    // Load today's reminders — ensure result is always an array
    api.getTodayReminders()
      .then(res => setReminders(Array.isArray(res) ? res : []))
      .catch(() => setReminders([]));
  }, []);

  const handleDeleteReminder = async (id) => {
    try {
      await api.deleteReminder(id);
      setReminders(rs => rs.filter(r => r.id !== id));
    } catch {}
  };

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (!data) return null;

  return (
    <div>
      <div className="page-header">
        <h2>Dashboard</h2>
        <p>Welcome back — here's your store overview for today</p>
      </div>

      {/* Today's Reminders — always visible */}
      <div className="card mb-6" style={{ borderLeft: '3px solid var(--accent)', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 7 }}>
            <NavIcon name="calendar" size={15} /> Today's Reminders
          </div>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}
          </span>
        </div>

        {/* Quick-add form */}
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const text = e.target.reminder.value.trim();
            if (!text) return;
            try {
              const today = new Date();
              const iso = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
              await api.createReminder(iso, text);
              e.target.reminder.value = '';
              const updated = await api.getTodayReminders();
              setReminders(Array.isArray(updated) ? updated : []);
            } catch (err) {
              const { default: toast } = await import('react-hot-toast');
              toast.error(err.message);
            }
          }}
          style={{ display: 'flex', gap: 8, marginBottom: reminders.length > 0 ? 12 : 0 }}
        >
          <input
            name="reminder"
            className="input"
            style={{ flex: 1, fontSize: 13, padding: '7px 10px' }}
            placeholder="Add a reminder for today…"
            maxLength={200}
          />
          <button type="submit" className="btn btn-primary" style={{ fontSize: 13, padding: '7px 14px', whiteSpace: 'nowrap' }}>
            + Add
          </button>
        </form>

        {reminders.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--muted)', padding: '6px 2px' }}>
            No reminders for today.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {reminders.map(r => (
              <div
                key={r.id}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 12px', background: 'var(--surface2)', borderRadius: 6, fontSize: 13,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: 'var(--accent)', fontSize: 14 }}>•</span>
                  {r.text}
                </div>
                <button
                  onClick={() => handleDeleteReminder(r.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--muted)', fontSize: 14, lineHeight: 1, padding: '2px 6px', borderRadius: 4 }}
                  title="Dismiss"
                >✕</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="stat-grid">
        <div className="stat-card green">
          <div className="label">Today's Revenue</div>
          <div className="value">{fmt.currency(data.todayRevenue)}</div>
          <div className="sub">{data.todayTransactions} transactions</div>
        </div>
        <div className="stat-card blue">
          <div className="label">This Month</div>
          <div className="value">{fmt.currency(data.monthRevenue)}</div>
          <div className="sub">{data.monthTransactions} transactions</div>
        </div>
        <div className="stat-card">
          <div className="label">Total Products</div>
          <div className="value" style={{color:'var(--text)'}}>{data.totalProducts}</div>
          <div className="sub">in inventory</div>
        </div>
        <div className="stat-card warn">
          <div className="label">Low Stock</div>
          <div className="value">{data.lowStockCount}</div>
          <div className="sub">items need restocking</div>
        </div>
      </div>

      {data.lowStockItems?.length > 0 && (
        <div className="card mb-6">
          <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <NavIcon name="warning" size={14} /> Low Stock Alert
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Product</th><th>Current Stock</th><th>Unit</th></tr></thead>
              <tbody>
                {data.lowStockItems.map(p => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td><span className={`badge ${p.stock <= 0 ? 'badge-red' : 'badge-warn'}`}>{p.stock}</span></td>
                    <td className="text-muted">{p.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}      
    </div>
  );
}
